import React, { forwardRef, useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { lensProp, map, view } from "ramda";
import {
  getElementDimensions,
  applyTransform,
} from "biblioboard-pdf-viewer/es/util/domUtil";
import {
  BASE_TRANSFORM,
  calculateScaleThatFits,
  scaleWithinContainer,
  translateToCenter,
  translateWithinContainer,
} from "biblioboard-pdf-viewer/es/util/translationUtil";
import { notBlank } from "../util/ext";

import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";

import PanelLoadingIndicator from "../components/PanelLoadingIndicator";

const useStyles = makeStyles(theme => ({
  error: {
    color: theme.palette.error.main,
    display: "flex",
    justifyContent: "center",
  },

  image: {
    cursor: "grab",
    userSelect: "none",
  },

  viewerContainer: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    position: "relative",
    backgroundColor: theme.palette.grey[100],
    flex: 1,
    overflow: "hidden",
  },

  viewport: {
    position: "absolute",
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  },
}));

const loadImage = src => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      resolve(img);
    };
    img.onerror = img.onabort = function () {
      reject(src);
    };
    img.src = src;
  });
};

// show currentPage after all images have loaded
const useImagesLoaded = srcs => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    if (isCancelled) return;

    async function load() {
      try {
        await Promise.all(map(loadImage, srcs));
        if (isCancelled) return;
        setLoaded(true);
      } catch (err) {
        setError(true);
      }
    }

    load();

    return () => {
      isCancelled = true;
    };
  }, [srcs]);

  return [loaded, error];
};

const ImageBookViewer = forwardRef(
  (
    {
      pagesLoading,
      pages,
      currentPage,
      transform,
      setTransform,
      handlePreviousPage,
      handleNextPage,
    },
    ref,
  ) => {
    const classes = useStyles();
    const [loaded, error] = useImagesLoaded(
      map(view(lensProp("pageUrl")), pages),
    );

    const getViewportDimensions = useCallback(
      () => ref.viewport.current.getBoundingClientRect(),
      [ref.viewport],
    );
    const getImageDimensions = useCallback(
      () => getElementDimensions(ref.image.current),
      [ref.image],
    );

    useEffect(() => {
      if (ref.image.current) {
        applyTransform(transform, ref.image.current);
      }
    }, [transform, ref.image]);

    const transformImage = useCallback(
      event => {
        const viewportDimensions = getViewportDimensions();
        const imageDimensions = getImageDimensions();

        const initialTransform = BASE_TRANSFORM;

        const scaleThatFits = calculateScaleThatFits(
          viewportDimensions,
          imageDimensions,
          initialTransform,
        );

        const scale =
          transform.scale !== BASE_TRANSFORM.scale
            ? transform.scale
            : scaleThatFits < 1
            ? scaleThatFits
            : 1;

        const transformScaled = scaleWithinContainer(
          viewportDimensions,
          imageDimensions,
          scale,
          initialTransform,
        );

        const transformCentered = translateToCenter(
          viewportDimensions,
          imageDimensions,
          transformScaled,
        );

        setTransform(transformCentered);
      },
      [getImageDimensions, getViewportDimensions, transform, setTransform],
    );

    const [dragging, setDragging] = useState(false);
    const [prevMouseScreenCoords, setPrevMouseScreenCoords] = useState({
      x: 0,
      y: 0,
      pageX: 0,
      pageY: 0,
    });

    const handleImageMouseDown = e => {
      setDragging(true);
      setPrevMouseScreenCoords({
        x: e.screenX,
        y: e.screenY,
        pageX: e.pageX,
        pageY: e.pageY,
      });
    };

    const handleImageMouseUp = e => {
      setDragging(false);
      setPrevMouseScreenCoords({ x: 0, y: 0, pageX: 0, pageY: 0 });
    };

    const handleImageMove = e => {
      if (dragging) {
        const movement = {
          x: prevMouseScreenCoords.x - e.screenX,
          y: prevMouseScreenCoords.y - e.screenY,
        };

        setPrevMouseScreenCoords({
          x: e.screenX,
          y: e.screenY,
          pageX: e.pageX,
          pageY: e.pageY,
        });

        const newTransform = translateWithinContainer(
          getViewportDimensions(),
          getImageDimensions(),
          {
            x: transform.translate.x - movement.x,
            y: transform.translate.y - movement.y,
          },
          transform,
        );

        setTransform(newTransform);
      }
    };

    useEffect(() => {
      // stop dragging if mouse moves outside viewport
      const viewport = getViewportDimensions();
      if (
        prevMouseScreenCoords.pageY < viewport.y ||
        prevMouseScreenCoords.pageY > viewport.bottom ||
        prevMouseScreenCoords.x > viewport.right ||
        prevMouseScreenCoords.x < viewport.left
      ) {
        setDragging(false);
      }
    }, [getViewportDimensions, prevMouseScreenCoords]);

    //-- Global Events
    const handleKeyDown = useCallback(
      event => {
        if (event.key === "ArrowLeft") {
          handlePreviousPage();
        }

        if (event.key === "ArrowRight") {
          handleNextPage();
        }
      },
      [handlePreviousPage, handleNextPage],
    );

    useEffect(() => {
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [handleKeyDown]);

    useEffect(() => {
      window.addEventListener("resize", transformImage);
      return () => {
        window.removeEventListener("resize", transformImage);
      };
    }, [transformImage]);

    return (
      <div className={classes.viewerContainer}>
        <div
          ref={ref.viewport}
          className={classes.viewport}
          onWheel={handleImageMove}
        >
          {error && (
            <Typography className={classes.error}>
              An error has occurred. Please try again later. If the issue
              persists, please contact{" "}
              <Link href="mailto:support@biblioboard.com">
                support@biblioboard.com
              </Link>
            </Typography>
          )}
          {pagesLoading && !loaded && <PanelLoadingIndicator />}
          {loaded && notBlank(currentPage) && (
            <img
              className={classes.image}
              style={{ cursor: dragging ? "grabbing" : "grab" }}
              id={currentPage.logicalPageIndex}
              ref={ref.image}
              alt={`page ${currentPage.logicalPageIndex}`}
              src={currentPage.pageUrl}
              onLoad={transformImage}
              onDragStart={e => e.preventDefault()}
              onMouseDown={handleImageMouseDown}
              onMouseUp={handleImageMouseUp}
              onMouseMove={handleImageMove}
            />
          )}
        </div>
      </div>
    );
  },
);

export default ImageBookViewer;
